import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-2xl font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: 'bg-brand-500 text-white shadow-lg hover:bg-brand-600 hover:shadow-xl active:scale-[0.97] font-black border-2 border-brand-600',
        secondary: 'bg-white border-3 border-brand-500 text-brand-600 hover:bg-brand-50 hover:border-brand-600 active:scale-[0.97] font-black shadow-md',
        outline: 'bg-transparent border-2 border-brand-500 text-brand-600 hover:bg-brand-50 hover:border-brand-600 active:scale-[0.97] font-black',
        ghost: 'hover:bg-neutral-100 text-neutral-900 hover:text-brand-600 active:scale-[0.97] font-bold',
        danger: 'bg-danger-500 text-white hover:bg-red-600 active:scale-[0.97] border-2 border-red-600 shadow-lg',
      },
      size: {
        sm: 'h-10 px-5 text-sm',
        md: 'h-12 px-7 text-base',
        lg: 'h-16 px-9 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconLeft, iconRight, loading, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={loading || props.disabled}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        ) : iconLeft && <span className="mr-2">{iconLeft}</span>}
        {children}
        {iconRight && !loading && <span className="ml-2">{iconRight}</span>}
      </button>
    );
  }
);
Button.displayName = 'Button';

